import React from 'react';
import { createPortal } from 'react-dom';
import { Box, Check, Code2, Copy, Database, Lock, RadioTower, Star } from 'lucide-react';
import clsx, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// cx merges Tailwind classes (clsx + tailwind-merge) so later utilities win.
export const cx = (...inputs: ClassValue[]): string => twMerge(clsx(inputs));

// Shared types + presentational atoms for the additive git-platform pages that
// live alongside RepoView (the repo browser): RepoHub (list), PullsInbox
// (/pulls) and Profile (/u/:handle). Keeping them here means those surfaces
// render repo cards, state badges and metrics identically.

export interface MeResponse {
  authenticated: boolean;
  user?: { id: string; email: string };
  handle?: string;
}

export interface RepoCounts {
  openIssues: number;
  openPulls: number;
  branches: number;
  commits: number;
}

export interface RepoSummary {
  id: string;
  fullName: string;
  owner: string;
  name: string;
  description: string;
  visibility: string;
  defaultBranch: string;
  counts: RepoCounts;
  updatedAt?: string;
  imageUrl?: string;
  ownerAvatar?: string;
  ownerHandle?: string;
  kind?: string;
  topics?: string[];
  homepage?: string;
  stars?: number;
  viewerHasStarred?: boolean;
}

export interface RepoPull {
  number: number;
  title: string;
  sourceBranch: string;
  targetBranch: string;
  state: string;
}

export interface InboxPull extends RepoPull {
  id: string;
  repoId: string;
  repoFullName: string;
  repoVisibility: string;
  updatedAt?: string;
}

export interface ProfileApp {
  type: 'site' | 'model';
  name: string;
  slug?: string;
  url: string;
  status: string;
  hardware?: string;
  updatedAt?: string;
}

export interface ProfileContributions {
  total: number;
  days: Record<string, number>;
  start: string;
  end: string;
}

export interface ProfileData {
  handle: string;
  displayName?: string;
  avatar?: string;
  bio: string;
  joinedAt: string;
  isSelf: boolean;
  following: boolean;
  counts: { repos: number; apps?: number; posts?: number; followers: number; following: number };
  repos: RepoSummary[];
  apps?: ProfileApp[];
  contributions?: ProfileContributions;
}

// repoHref builds the pretty owner/name URL that RepoView routes on, so every
// additive page links into the repo browser the same way.
export function repoHref(repo: { fullName: string }, view = ''): string {
  return view ? `/repos/${repo.fullName}/${view}` : `/repos/${repo.fullName}`;
}

export type RepoCloneProtocol = 'https' | 'ssh';

const SAFE_REPO_FULL_NAME = /^[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+$/;

function safeRepoFullName(fullName: string): string {
  const trimmed = fullName.trim();
  if (!SAFE_REPO_FULL_NAME.test(trimmed)) {
    return '';
  }
  return trimmed;
}

function cloneOrigin(origin?: string): string {
  return (origin || (typeof window !== 'undefined' ? window.location.origin : 'https://app.nz')).replace(/\/$/, '');
}

function cloneHost(origin?: string): string {
  try {
    return new URL(cloneOrigin(origin)).host;
  } catch {
    return 'app.nz';
  }
}

export function repoHTTPSCloneUrl(repo: { fullName: string }, origin?: string): string {
  const fullName = safeRepoFullName(repo.fullName);
  if (!fullName) return '';
  return `${cloneOrigin(origin)}/${fullName}.git`;
}

export function repoSSHCloneUrl(repo: { fullName: string }, origin?: string): string {
  const fullName = safeRepoFullName(repo.fullName);
  if (!fullName) return '';
  return `ssh://git@${cloneHost(origin)}/${fullName}.git`;
}

export function repoCloneUrl(repo: { fullName: string }, origin?: string, protocol: RepoCloneProtocol = 'https'): string {
  return protocol === 'ssh' ? repoSSHCloneUrl(repo, origin) : repoHTTPSCloneUrl(repo, origin);
}

export function repoCloneCommand(repo: { fullName: string }, origin?: string, protocol: RepoCloneProtocol = 'https'): string {
  const url = repoCloneUrl(repo, origin, protocol);
  return url ? `git clone ${url}` : '';
}

export function VisibilityIcon({ visibility, size = 14 }: { visibility: string; size?: number }) {
  return visibility === 'private' ? <Lock size={size} /> : <RadioTower size={size} />;
}

// Deterministic colour for the initial-letter fallback avatar.
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return `hsl(${h} 48% 42%)`;
}

// OwnerAvatar renders the owner's Gravatar when present, falling back to a
// branded coloured initial when there's no image or the image 404s.
export function OwnerAvatar({ name, src, size = 22 }: { name: string; src?: string; size?: number }) {
  const [failed, setFailed] = React.useState(false);
  const seed = name || '?';
  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setFailed(true)}
        className="shrink-0 rounded-full object-cover ring-1 ring-white/15"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white ring-1 ring-white/15"
      style={{ width: size, height: size, background: avatarColor(seed), fontSize: size * 0.46 }}
    >
      {seed.trim().charAt(0).toUpperCase()}
    </span>
  );
}

const STATE_STYLES: Record<string, string> = {
  open: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  closed: 'border-red-500/40 bg-red-500/10 text-red-300',
  merged: 'border-violet-500/40 bg-violet-500/10 text-violet-300',
  draft: 'border-white/20 bg-white/[0.06] text-white/60',
};

export function StateBadge({ state }: { state: string }) {
  const cls = STATE_STYLES[state] || STATE_STYLES.draft;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${cls}`}>
      {state}
    </span>
  );
}

export function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="min-w-[68px] rounded-lg border border-white/10 bg-black px-3 py-2">
      <div className="font-display text-lg font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-white/34">{label}</div>
    </div>
  );
}

export function ListEmpty({ visible, children }: { visible: boolean; children: React.ReactNode }) {
  if (!visible) return null;
  return <p className="rounded-lg border border-dashed border-white/10 px-3 py-6 text-center text-sm text-white/34">{children}</p>;
}

export function Alert({ error, notice }: { error?: string; notice?: string }) {
  return (
    <>
      {error && <p className="mb-4 rounded-lg border border-red-900 bg-red-950/60 px-4 py-3 text-sm text-red-200">{error}</p>}
      {notice && <p className="mb-4 rounded-lg border border-emerald-900 bg-emerald-950/60 px-4 py-3 text-sm text-emerald-100">{notice}</p>}
    </>
  );
}

type RepoContextAction = {
  left: number;
  top: number;
  copied?: RepoCloneProtocol;
};

function clampContextAction(clientX: number, clientY: number) {
  if (typeof window === 'undefined') return { left: clientX, top: clientY };
  const width = 244;
  const height = 104;
  const margin = 12;
  const maxLeft = Math.max(margin, window.innerWidth - width - margin);
  const maxTop = Math.max(margin, window.innerHeight - height - margin);
  return {
    left: Math.min(Math.max(clientX + 10, margin), maxLeft),
    top: Math.min(Math.max(clientY + 10, margin), maxTop),
  };
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    if (typeof document === 'undefined') return false;
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

// RepoCard is the shared repo tile used by both the hub and profile pages.
// kindMeta maps a repo kind to its badge label, icon, and accent classes so the
// hub can distinguish code, Hugging Face datasets, and models at a glance.
export function kindMeta(kind?: string): { label: string; Icon: typeof Code2; cls: string } {
  switch (kind) {
    case 'dataset':
      return { label: 'Dataset', Icon: Database, cls: 'border-amber-400/30 bg-amber-400/10 text-amber-200' };
    case 'model':
      return { label: 'Model', Icon: Box, cls: 'border-violet-400/30 bg-violet-400/10 text-violet-200' };
    default:
      return { label: 'Code', Icon: Code2, cls: 'border-sky-400/30 bg-sky-400/10 text-sky-200' };
  }
}

export function KindBadge({ kind, size = 11 }: { kind?: string; size?: number }) {
  if (!kind || kind === 'code') return null;
  const { label, Icon, cls } = kindMeta(kind);
  return (
    <span className={cx('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide', cls)}>
      <Icon size={size} /> {label}
    </span>
  );
}

export function RepoCard({ repo }: { repo: RepoSummary }) {
  const [contextAction, setContextAction] = React.useState<RepoContextAction | null>(null);
  const openTimer = React.useRef<number | null>(null);
  const closeTimer = React.useRef<number | null>(null);
  const httpsCommand = repoCloneCommand(repo);
  const sshCommand = repoCloneCommand(repo, undefined, 'ssh');

  React.useEffect(() => {
    if (!contextAction || typeof window === 'undefined') return;
    const close = () => setContextAction(null);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKeyDown);
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(close, contextAction.copied ? 1800 : 8000);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKeyDown);
      if (closeTimer.current) {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
    };
  }, [contextAction]);

  React.useEffect(() => {
    return () => {
      if (openTimer.current && typeof window !== 'undefined') window.clearTimeout(openTimer.current);
    };
  }, []);

  const onContextMenu = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (typeof window === 'undefined') return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX || rect.left + 16;
    const y = event.clientY || rect.top + 16;
    const pos = clampContextAction(x, y);
    if (openTimer.current) window.clearTimeout(openTimer.current);
    openTimer.current = window.setTimeout(() => {
      setContextAction(pos);
    }, 0);
  };

  const cloneAction =
    contextAction && typeof document !== 'undefined'
      ? createPortal(
          <div
            className="fixed z-[1000] rounded-lg border border-white/15 bg-black/95 p-1 shadow-2xl shadow-black/50 backdrop-blur"
            style={{ left: contextAction.left, top: contextAction.top }}
            role="menu"
            aria-label={`${repo.fullName} repository actions`}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {([
              ['https', 'Copy clone command', 'Copied clone command', httpsCommand],
              ['ssh', 'Copy SSH clone command', 'Copied SSH clone command', sshCommand],
            ] as const).map(([protocol, label, copiedLabel, command]) => (
              <button
                key={protocol}
                type="button"
                className="flex h-11 min-w-[224px] items-center gap-3 rounded-md px-3 text-left text-sm font-semibold text-white/86 transition-colors hover:bg-white/10 focus:bg-white/10 focus:outline-none"
                onClick={async (event) => {
                  event.stopPropagation();
                  const copied = await copyText(command);
                  setContextAction((current) => (current ? { ...current, copied: copied ? protocol : undefined } : current));
                }}
              >
                {contextAction.copied === protocol ? <Check size={16} className="text-emerald-300" /> : <Copy size={16} className="text-white/58" />}
                <span>{contextAction.copied === protocol ? copiedLabel : label}</span>
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <a
        href={repoHref(repo)}
        data-testid={`repo-card-${repo.id}`}
        data-repo-full-name={repo.fullName}
        onContextMenu={onContextMenu}
        className="group flex min-w-0 flex-col overflow-hidden rounded-xl border border-white/10 bg-white/[0.035] transition-colors hover:border-white/30 hover:bg-white/[0.06]"
      >
        {repo.imageUrl ? (
          <div className="aspect-[16/7] w-full overflow-hidden border-b border-white/10 bg-black">
            <img
              src={repo.imageUrl}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </div>
        ) : null}
        <div className="min-w-0 p-4">
          <div className="flex items-center gap-2 text-white/48">
            <OwnerAvatar name={repo.owner} src={repo.ownerAvatar} size={20} />
            <span className="truncate font-mono text-xs text-white/56">{repo.owner}</span>
            <VisibilityIcon visibility={repo.visibility} />
            <span className="ml-auto"><KindBadge kind={repo.kind} /></span>
          </div>
          <h2 className="mt-1.5 truncate font-display text-lg font-bold text-white group-hover:underline">{repo.name}</h2>
          <p className="mt-1 line-clamp-2 min-h-[2.5rem] break-words text-sm leading-6 text-white/52">
            {repo.description || 'No description yet.'}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-white/46">
            {repo.stars ? (
              <span className="inline-flex items-center gap-1 font-mono text-amber-200/80"><Star size={12} /> {repo.stars}</span>
            ) : null}
            <span className="font-mono">{repo.counts.openIssues} issues</span>
            <span className="font-mono">{repo.counts.openPulls} PRs</span>
            <span className="font-mono">{repo.counts.commits} commits</span>
          </div>
        </div>
      </a>
      {cloneAction}
    </>
  );
}

// Shared auth gate: returns a loading/redirect node, or null when the signed-in
// user is ready and the page can render.
export function useRequireAuth(me: MeResponse | null, next: string): React.ReactNode | null {
  if (!me) {
    return (
      <section className="min-h-screen bg-black pt-28 text-white">
        <div className="mx-auto max-w-[1800px] px-6 text-white/50">Loading…</div>
      </section>
    );
  }
  if (!me.authenticated) {
    if (typeof window !== 'undefined') window.location.href = `/login?next=${encodeURIComponent(next)}`;
    return null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Canonical design tokens for the platform "cloud" surfaces (agents, repos,
// pulls, builds, cogs, sites, assistants). Everything is black + white with
// colour reserved for status. Studios should compose from UI / StatusBadge
// instead of hand-rolling slate/cyan classes so the whole app stays uniform.
// ---------------------------------------------------------------------------

export const UI = {
  page: 'min-h-screen bg-black text-white',
  panel: 'rounded-xl border border-white/10 bg-white/[0.04]',
  panelHover: 'transition-colors hover:border-white/30 hover:bg-white/[0.06]',
  card: 'rounded-xl border border-white/10 bg-white/[0.035]',
  input:
    'h-9 w-full rounded-lg border border-white/10 bg-black px-3 text-sm text-white outline-none placeholder:text-white/30 focus:border-white/40',
  textarea:
    'w-full resize-y rounded-lg border border-white/10 bg-black px-3 py-2 text-sm text-white outline-none placeholder:text-white/30 focus:border-white/40',
  btn: 'inline-flex h-9 items-center justify-center gap-2 rounded-lg px-4 text-sm font-bold transition-colors disabled:opacity-60',
  btnPrimary: 'bg-sky-400 text-[#1a1408] hover:bg-sky-300',
  btnGhost: 'border border-white/10 bg-white/[0.06] text-white hover:bg-white/[0.1]',
  btnOutline: 'border border-white/15 text-white hover:border-white/40 hover:bg-white/[0.04]',
  label: 'text-[10px] font-bold uppercase tracking-[0.16em] text-white/40',
  heading: 'font-display font-bold tracking-tight',
  muted: 'text-white/50',
  faint: 'text-white/35',
} as const;

// One status vocabulary for every studio. Keys are normalised lowercase status
// strings; synonyms are aliased so agent/build/cog/PR states share colours.
const STATUS_TONES: Record<string, string> = {
  // neutral / queued
  queued: 'border-white/15 bg-white/[0.06] text-white/70',
  pending: 'border-white/15 bg-white/[0.06] text-white/70',
  idle: 'border-white/15 bg-white/[0.06] text-white/70',
  draft: 'border-white/15 bg-white/[0.06] text-white/60',
  idea: 'border-white/15 bg-white/[0.06] text-white/60',
  todo: 'border-white/15 bg-white/[0.06] text-white/60',
  // in-flight (warm)
  provisioning: 'border-amber-500/30 bg-amber-500/12 text-amber-300',
  building: 'border-amber-500/30 bg-amber-500/12 text-amber-300',
  starting: 'border-amber-500/30 bg-amber-500/12 text-amber-300',
  processing: 'border-amber-500/30 bg-amber-500/12 text-amber-300',
  // running (active blue)
  running: 'border-sky-500/30 bg-sky-500/12 text-sky-300',
  in_progress: 'border-sky-500/30 bg-sky-500/12 text-sky-300',
  ci: 'border-sky-500/30 bg-sky-500/12 text-sky-300',
  // review
  review: 'border-amber-500/30 bg-amber-500/12 text-amber-300',
  pr_review: 'border-amber-500/30 bg-amber-500/12 text-amber-300',
  // success / open / cron
  open: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  done: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  succeeded: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  ready: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  pushed: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  passed: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  cron: 'border-emerald-500/30 bg-emerald-500/12 text-emerald-300',
  // merged (violet)
  merged: 'border-violet-500/30 bg-violet-500/12 text-violet-300',
  // failure
  failed: 'border-red-500/30 bg-red-500/12 text-red-300',
  error: 'border-red-500/30 bg-red-500/12 text-red-300',
  closed: 'border-red-500/30 bg-red-500/12 text-red-300',
  // stopped
  cancelled: 'border-white/12 bg-white/[0.04] text-white/45',
  canceled: 'border-white/12 bg-white/[0.04] text-white/45',
  skipped: 'border-white/12 bg-white/[0.04] text-white/45',
};

const STATUS_NEUTRAL = 'border-white/15 bg-white/[0.06] text-white/60';

// statusTone returns the capsule classes for a status string (case/space/dash
// insensitive). Use when you need the raw classes; prefer StatusBadge for UI.
export function statusTone(status: string): string {
  const key = (status || '').toLowerCase().trim().replace(/[\s-]+/g, '_');
  return STATUS_TONES[key] || STATUS_NEUTRAL;
}

// StatusBadge is the canonical status capsule used across every studio.
export function StatusBadge({
  status,
  label,
  className,
}: {
  status: string;
  label?: string;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize',
        statusTone(status),
        className,
      )}
    >
      {(label ?? status).replace(/_/g, ' ')}
    </span>
  );
}
